import { useCallback } from 'react';
import { useDepartment } from '../contexts/DepartmentContext';

/**
 * "May I manage THIS order?" — asked the way the server asks it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS REPLACED, AND WHY IT WAS WRONG
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every order-management control on DepartmentOrders, Approvals and MyOrders
 * used to render behind `(isAdmin || isBuyer)`. `isBuyer` reaches the client
 * from `GET /api/roles/my-assignments` and originates in
 * `server/middleware/departmentScope.js` from the caller's SINGLE UNSCOPED
 * `user_roles` row (`user_roles.user_id` is UNIQUE, so there is exactly one and
 * it is not per department).
 *
 * A correctly-created department buyer — `department_assignments.role = 'buyer'`
 * and NO `user_roles` row, which is what the role model requires — therefore has
 * `isBuyer === false` and was shown a crippled interface, while
 * `PATCH /api/orders/:id/po-number` would have accepted the write. Found on
 * staging 2026-08-06 when a prospective client's department was seeded correctly
 * for the first time; the defect had been MASKED because the signup wizard
 * writes an unscoped `buyer` row for everyone (docs/ROLES.md §F.1 row 6), so
 * ordinary users satisfied the check by accident.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULE THIS ENCODES
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The client gate must ask the SAME QUESTION as the server gate for the SAME
 * ACTION. For PO number, status, admin notes and the accounting line-item
 * fields that is, verbatim:
 *
 *     requireDepartmentCapability(req, res, order.department_id,
 *                                 'canManageDepartmentOrders')
 *
 * — resolved in THE ORDER'S OWN department, not the caller's active one. That
 * distinction is not pedantry: `GET /api/orders?user_id=` returns the caller's
 * orders across every department (qualified below), so that page routinely shows
 * orders from a department the viewer is not standing in. Hence
 * `capabilitiesFor(order.department_id)` rather than the active department's
 * `capabilities`.
 *
 * 🔻 **THE APPROVALS HALF OF THAT SENTENCE EXPIRED ON 2026-08-12 — it read
 * "`GET /api/approvals/pending` is not department-scoped at all (it selects on
 * `approver_user_id`)", which was true and deliberate for months and is now
 * false.** Product owner: *"only site admin sees more than one department at a
 * time i think. but all other users, buyers, supervisors etc they should only
 * see things related to the production/department they select form the drop
 * down"*. That endpoint now carries
 * `helpers/pendingApprovalsScope.js`'s department filter.
 *
 * 🚨 **THE RULE THIS DOCBLOCK TEACHES SURVIVES BOTH EXPIRIES INTACT, AND THAT IS
 * THE POINT OF CORRECTING THE PREMISE RATHER THAN DELETING THE PARAGRAPH.** The
 * argument for `capabilitiesFor(order.department_id)` was never "the list spans
 * departments" — that was the most VIVID reason, not the load-bearing one. The
 * load-bearing reason is that the client gate must ask the same question as the
 * server gate, and the server resolves in the ORDER'S OWN department whatever is
 * in the list. **Do not "simplify" this hook to the active department's
 * `capabilities` now that the vivid reason is gone.** It also remains untrue in
 * fact for a SITE ADMIN, whose approvals list still spans departments by the
 * stated exception.
 *
 * ⚠️ THE SECOND HALF OF THAT SENTENCE IS UNQUALIFIED AND SHOULD NOT BE QUOTED
 * AS IT STANDS — corrected 2026-08-10, after it was quoted verbatim into a task
 * brief as the reason a server-side fix had to avoid department scoping.
 * `GET /api/orders?user_id=` spans every department ONLY WHEN NO
 * `x-department-id` HEADER IS SENT. `routes/orders.js` adds
 * `(o.department_id = ? OR o.department_id IS NULL)` to that path whenever the
 * header resolves, and `utils/api.js` sends the header on every request where
 * `localStorage.sfx_active_department_id` is set — which is essentially every
 * real MyOrders request. `server/tests/api/multi-production.test.js` records the
 * truth in a comment on its own cross-department case ("the cross-user's Props
 * orders may be filtered by the active department header").
 *
 * 🔑 THE RULE THIS DOCBLOCK TEACHES IS UNCHANGED, WHICH IS WHY THE CLAIM IS
 * QUALIFIED RATHER THAN DELETED. `capabilitiesFor(order.department_id)` is
 * still correct: the header can legitimately be absent (a fresh profile, a
 * direct navigation, a user with no active department chosen), an order with a
 * NULL `department_id` survives that narrowing and belongs to no department at
 * all, and Approvals spans departments for a site admin. (⚠️ That last clause
 * read *"Approvals genuinely spans departments unconditionally"* until
 * 2026-08-12 — see the correction above; the **unconditionally** is what died,
 * not the conclusion.) The active department's `capabilities` would still be the
 * wrong question.
 *
 * ⚠️ THIS IS A UI CONVENIENCE, NEVER A GATE. The client does not compute
 * authority; it reports an answer the server already resolved through
 * `departmentAuthority` and attached to the assignment row. Bypassing it buys
 * nothing — every one of these actions is refused server-side per department.
 * Same standing as `DepartmentContext.canViewAccounting` (CLAUDE.md, "Colour
 * Theme & Dark Mode").
 *
 * ⚠️ MIGRATING AN `isBuyer` SITE IS NOT A PURE NARROWING (docs/ROLES.md §C.2).
 * It NARROWS for a caller whose unscoped row is `buyer` but who holds nothing
 * here, and it WIDENS for a caller whose unscoped row is `user` while their
 * `department_assignments.role` is `buyer` HERE. Both directions are pinned by
 * `client/src/pages/orderSurfaces.capabilities.test.js` §2 and §3 — the diff
 * reads as a tightening on every line, which is how a widening ships
 * unnoticed. The payload behind them is pinned by
 * `server/tests/regressions/my-assignments-department-buyer-capabilities.test.js`.
 *
 * FAILS CLOSED. An order with no `department_id`, an order in a department the
 * caller holds no assignment in, and a caller with no assignments at all all
 * yield `NO_CAPABILITIES` (`{}`), so `.canManageDepartmentOrders` is `undefined`
 * — falsy. That matches `departmentAuthority`'s EMPTY_AUTHORITY for the same
 * three cases.
 *
 * ✅ FORMERLY-NOT-COVERED GATES, MIGRATED in tranche 3 increment 3d (2026-08-14)
 * — both server routes now resolve `canManageDepartmentOrders` in the ORDER'S
 * OWN department, so both are answerable by `canManageOrder` (plus the owner /
 * Stores / admin disjuncts the routes carry, resolved by the page):
 *   - Cancel            → `PATCH /api/orders/:id/cancel` (the SYNC IIFE that
 *                         folded the any-department `req.isBuyer` with a
 *                         membership test was DELETED, not made async, so it
 *                         could not fail open). Reached client-side through the
 *                         status override, already gated on `canManageOrder`.
 *   - Upload paperwork  → `POST /api/orders/:id/files?category=delivery`. The
 *                         page now gates it identically to the received gate
 *                         (`isAdmin || canManageOrder(order) || owner || Stores`).
 * There is no longer an order-management gate this hook deliberately declines.
 *
 * ⚠️ **"Restore / uncancel" WAS A THIRD ENTRY HERE AND IS GONE (2026-08-12) —
 * the ROUTE was deleted, not migrated.** It is not waiting on anything and has
 * no successor gate; leaving `cancelled` is `PATCH /:id/status`'s job, which
 * this hook DOES answer through `canManageOrder`. Recorded because a
 * not-covered list is read as a to-do list, and an entry naming a deleted
 * route sends the next agent hunting for a migration that will never happen.
 */
export function useOrderAuthority() {
  const { isAdmin, capabilitiesFor } = useDepartment();

  /**
   * @param {{ department_id?: number|string|null }|null|undefined} order
   * @returns {boolean} whether the order-management controls should render.
   */
  const canManageOrder = useCallback((order) => {
    // ─────────────────────────────────────────────────────────────────────
    // 🔀 THE SERVER-COMPUTED FLAG WINS WHEN IT IS PRESENT (2026-09-11)
    // ─────────────────────────────────────────────────────────────────────
    // `GET /api/orders` and `GET /api/orders/:id` now report `can_manage`,
    // computed by `server/helpers/orderManagementAuthority.js` — the SAME
    // predicate the lifecycle routes gate on.
    //
    // 🚨 THE CLIENT COULD NOT GET IT RIGHT BY TRYING HARDER, which is the
    // `helpers/productionEditAuthority.js` precedent (*"the client could not
    // have got it right by trying harder"*). Owner ruling, 2026-09-11:
    // *"Mysapce is a personal space"* — a My Space order's owner may run its
    // whole lifecycle even though they are seated there as role `user`. The
    // predicate's owner arm needs `productions.is_personal`,
    // `productions.owner_user_id` and `orders.user_id`, reached by JOIN from
    // the order's department. The client holds NONE of those three: the
    // assignment rows behind `capabilitiesFor` carry capabilities, not
    // ownership, and `orders.production_id` is untrustworthy anyway (20 dev
    // rows disagree with their department). So the answer comes from the
    // server or not at all.
    //
    // 🔑 THE FALLBACK IS NOT DEAD CODE AND MUST STAY — AND A PAGE CAN READ
    // BOTH SOURCES AT ONCE. ⚠️ CORRECTED 2026-09-11, after this comment (and a
    // status report) claimed `DepartmentOrders.js` and `Approvals.js` "keep the
    // capability derivation, UNCHANGED". That is FALSE at the DETAIL level.
    // Which source a call site reads is decided by WHAT IT PASSES, not by which
    // page it is on:
    //   • `canManageOrder(order)` — a LIST row from `GET /api/orders/department`
    //     or `GET /api/approvals/pending`, neither of which computes
    //     `can_manage`. `can_manage` is `undefined`, so these fall through to
    //     `capabilitiesFor(order.department_id)`. DepartmentOrders' three
    //     status-progression menu items are the live examples.
    //   • `canManageOrder(detail)` — the payload of `GET /api/orders/:id`, which
    //     DOES carry `can_manage`, so these now read the FLAG. That is
    //     DepartmentOrders' `statusJumpNode`, `adminNotes`, `canReceiveItems`
    //     and `canManage`, and every one of Approvals' gates, which are all
    //     detail-level.
    //
    // 🟢 THE TWO SOURCES AGREE IN A REAL DEPARTMENT, WHICH IS WHY THE OWNER'S
    // *"NO access, state change etc changes for any user in a normal
    // department"* still holds. `canManageOrder(req, order)` on the server is
    // `isGlobalAdmin || canManageDepartmentOrders || isOwnPersonalSpaceOrder`,
    // and the third disjunct requires the order's department to hang off a
    // production with `is_personal = 1` — unreachable outside My Space. So in a
    // non-personal department the flag's value IS the capability the derivation
    // computes, plus the same admin short-circuit the line below carries.
    // 🪤 They can only diverge on a personal order, and only one page lists
    // those. When a third endpoint starts reporting the flag, its row-level
    // sites move onto it for free.
    //
    // 🪤 `=== true`, never truthy. A flag that arrives as a Promise (a missed
    // `await` on the server) JSON-serialises to `{}`, which is truthy; a flag
    // that arrives `undefined` must fall through to the derivation rather than
    // read as `false`, which is why the test is `!== undefined` and not a
    // truthiness check on the value.
    if (order && order.can_manage !== undefined) return order.can_manage === true;
    // Global site admin is the one legitimate site-wide tier, and the server
    // short-circuits on it before any column is read
    // (server/helpers/departmentAuthority.js). Keep the two in step.
    if (isAdmin) return true;
    return capabilitiesFor(order?.department_id).canManageDepartmentOrders === true;
  }, [isAdmin, capabilitiesFor]);

  return { canManageOrder };
}

export default useOrderAuthority;
